/**
 * Google OAuth Authentication Service
 * Handles user authentication and session management
 * Supports both web (Google Identity Services) and native (Capacitor plugin) auth
 */

import { logger } from '../utils/logger';
import { detectPlatform } from '../utils/platform';
import { GoogleAuth } from '@southdevs/capacitor-google-auth';

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  verified_email: boolean;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: GoogleUser | null;
  isLoading: boolean;
  error: string | null;
}

export interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

class AuthService {
  private isInitialized = false;
  private clientId: string;
  private platform: 'web' | 'android' | 'ios';

  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    this.platform = detectPlatform();
    if (!this.clientId && process.env.NODE_ENV === 'production') {
      logger.error('REACT_APP_GOOGLE_CLIENT_ID is required for authentication');
    }
  }

  /**
   * Initialize Google Auth - uses native plugin on Android/iOS, web SDK on browser
   */
  async initialize(): Promise<void> {
    logger.debug('[Auth] initialize called', {
      isInitialized: this.isInitialized,
      clientId: this.clientId ? `${this.clientId.substring(0, 20)}...` : 'not set',
      platform: this.platform
    });

    if (this.isInitialized || !this.clientId) {
      logger.debug('[Auth] skipping init - already initialized or no clientId');
      return;
    }

    // For native platforms, initialize the Capacitor Google Auth plugin
    if (this.platform === 'android' || this.platform === 'ios') {
      try {
        logger.debug('[Auth] Initializing native GoogleAuth plugin...');
        // On Android, don't pass clientId - it uses server_client_id from strings.xml
        // On iOS, we would need to pass it or configure it in Info.plist
        await GoogleAuth.initialize({
          scopes: ['profile', 'email'],
          grantOfflineAccess: false,
        });
        this.isInitialized = true;
        logger.debug('[Auth] Native Google Auth initialized successfully', { platform: this.platform });
        return;
      } catch (error) {
        logger.error('[Auth] Failed to initialize native Google Auth', error);
        // Fall through to web initialization as fallback
      }
    }

    // Web platform: Load Google Identity Services script
    return new Promise((resolve, reject) => {
      if (!document.querySelector('script[src*="accounts.google.com"]')) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;

        script.onload = () => {
          this.isInitialized = true;
          resolve();
        };

        script.onerror = () => {
          reject(new Error('Failed to load Google Identity Services'));
        };

        document.head.appendChild(script);
      } else {
        this.isInitialized = true;
        resolve();
      }
    });
  }

  /**
   * Native sign-in using Capacitor Google Auth plugin
   */
  async nativeSignIn(): Promise<GoogleUser> {
    logger.debug('[Auth] nativeSignIn called', {
      platform: this.platform,
      isInitialized: this.isInitialized,
      clientId: this.clientId ? 'set' : 'not set'
    });

    try {
      logger.debug('[Auth] Calling GoogleAuth.signIn()...');
      const result = await GoogleAuth.signIn({
        scopes: ['profile', 'email'],
      });
      logger.debug('[Auth] GoogleAuth.signIn returned', {
        hasId: !!result?.id,
        hasEmail: !!result?.email,
        hasName: !!result?.name,
        hasAuth: !!result?.authentication,
        fullResult: JSON.stringify(result)
      });

      // Validate result
      if (!result) {
        throw new Error('No result returned from GoogleAuth.signIn');
      }
      if (!result.email) {
        throw new Error('No email returned from Google Sign-In');
      }
      if (!result.id) {
        throw new Error('No user ID returned from Google Sign-In');
      }

      const user: GoogleUser = {
        id: result.id,
        email: result.email,
        name: result.name || result.email,
        picture: result.imageUrl,
        verified_email: true, // Native auth implies verified
      };

      // Store user session (using the ID token from native auth)
      this.storeUserSession(user, result.authentication?.idToken || 'native-auth');
      logger.debug('[Auth] User session stored successfully', { email: user.email });

      return user;
    } catch (error: any) {
      logger.error('[Auth] nativeSignIn error', {
        message: error?.message,
        code: error?.code,
        error: String(error)
      });
      // User cancelled or error occurred
      if (error?.message?.includes('canceled') || error?.message?.includes('cancelled')) {
        throw new Error('Sign-in cancelled');
      }
      throw new Error(`Native sign-in failed: ${error?.message || error}`);
    }
  }

  /**
   * Initialize Google Sign-In button
   */
  initializeSignIn(
    elementId: string,
    onSuccess: (user: GoogleUser) => void,
    onError: (error: string) => void
  ): void {
    if (!this.isInitialized || !window.google) {
      onError('Google Identity Services not initialized');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response: GoogleCredentialResponse) => {
        this.handleCredentialResponse(response)
          .then(onSuccess)
          .catch(onError);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    window.google.accounts.id.renderButton(
      document.getElementById(elementId)!,
      {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        shape: 'rectangular',
        width: 250,
      }
    );
  }

  /**
   * Handle Google credential response
   */
  private async handleCredentialResponse(response: GoogleCredentialResponse): Promise<GoogleUser> {
    try {
      const payload = this.parseJWT(response.credential);

      const user: GoogleUser = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        verified_email: payload.email_verified
      };

      // Store user session
      this.storeUserSession(user, response.credential);

      return user;
    } catch (error) {
      throw new Error(`Authentication failed: ${error}`);
    }
  }

  /**
   * Parse JWT token to extract user info
   */
  private parseJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new Error('Invalid token format');
    }
  }

  /**
   * Store user session in localStorage
   */
  private storeUserSession(user: GoogleUser, token: string): void {
    const session = {
      user,
      token,
      timestamp: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
    };

    localStorage.setItem('auth_session', JSON.stringify(session));
    localStorage.setItem('userEmail', user.email); // Backward compatibility
  }

  /**
   * Get stored user session
   */
  getStoredSession(): { user: GoogleUser; token: string } | null {
    try {
      const sessionData = localStorage.getItem('auth_session');
      if (!sessionData) return null;

      const session = JSON.parse(sessionData);

      // Check if session is expired
      if (session.expiresAt && Date.now() > session.expiresAt) {
        this.clearSession();
        return null;
      }

      return {
        user: session.user,
        token: session.token
      };
    } catch (error) {
      logger.auth.debug('Failed to parse stored session', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Sign out user
   */
  async signOut(): Promise<void> {
    try {
      // Native platforms: sign out from Capacitor plugin
      if (this.platform === 'android' || this.platform === 'ios') {
        try {
          await GoogleAuth.signOut();
        } catch (error) {
          logger.auth.debug('Native sign out error (may be expected)', error);
        }
      }

      // Web platform: disable auto-select
      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }
      this.clearSession();
    } catch (error) {
      logger.auth.debug('Error during sign out', error);
      this.clearSession();
    }
  }

  /**
   * Check if running on native platform
   */
  isNativePlatform(): boolean {
    return this.platform === 'android' || this.platform === 'ios';
  }

  /**
   * Clear user session
   */
  clearSession(): void {
    localStorage.removeItem('auth_session');
    // Keep userEmail for backward compatibility during transition
    // localStorage.removeItem('userEmail');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getStoredSession();
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser(): GoogleUser | null {
    const session = this.getStoredSession();
    return session?.user || null;
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    const session = this.getStoredSession();
    return session?.token || null;
  }

  /**
   * Check if Google OAuth is configured
   */
  isConfigured(): boolean {
    return !!this.clientId;
  }

  /**
   * Trigger Google One Tap if user previously signed in
   */
  promptOneTap(onSuccess: (user: GoogleUser) => void, onError: (error: string) => void): void {
    if (!this.isInitialized || !window.google) return;

    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response: GoogleCredentialResponse) => {
        this.handleCredentialResponse(response)
          .then(onSuccess)
          .catch(onError);
      },
    });

    window.google.accounts.id.prompt((notification: any) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // One Tap not shown - user needs to click sign-in button
        logger.auth.debug('One Tap not available, user needs manual sign-in');
      }
    });
  }
}

// Global type definitions for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: Element, options: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

export const authService = new AuthService();